import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Newspaper, Mail, Inbox, Settings as SettingsIcon, LogOut, Plus, Trash2, Pencil, X, Flag, Award, Check } from "lucide-react";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { ImageUploader } from "@/components/admin/ImageUploader";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      <header className="border-b border-champagne/25 bg-ivory/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <p className="font-heading text-2xl tracking-[0.12em] text-ink">LEAH THAMES</p>
            <p className="label-eyebrow">Admin Studio</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-body text-sm text-ink/60 hidden sm:block">{user?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-champagne/40 px-5 py-2.5 font-body text-sm text-ink hover:bg-blush/15 transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <Tabs defaultValue="news">
          <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0 justify-start mb-8">
            <Tab value="news" icon={Newspaper} label="News & Blog" />
            <Tab value="results" icon={Flag} label="Latest Results" />
            <Tab value="sponsors" icon={Award} label="Sponsors" />
            <Tab value="newsletter" icon={Mail} label="Newsletter" />
            <Tab value="inquiries" icon={Inbox} label="Inquiries" />
            <Tab value="settings" icon={SettingsIcon} label="Settings" />
          </TabsList>

          <TabsContent value="news"><NewsManager /></TabsContent>
          <TabsContent value="results"><ResultsManager /></TabsContent>
          <TabsContent value="sponsors"><SponsorManager /></TabsContent>
          <TabsContent value="newsletter"><NewsletterList /></TabsContent>
          <TabsContent value="inquiries"><InquiriesList /></TabsContent>
          <TabsContent value="settings"><SettingsPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Tab({ value, icon: Icon, label }) {
  return (
    <TabsTrigger value={value} data-testid={`admin-tab-${value}`}
      className="rounded-full border border-champagne/40 bg-ivory px-5 py-2.5 font-body text-sm text-ink data-[state=active]:bg-blush data-[state=active]:border-bronze transition-colors gap-2">
      <Icon size={15} /> {label}
    </TabsTrigger>
  );
}

const inputCls = "w-full rounded-xl bg-white border border-champagne/40 px-4 py-3 font-body text-sm focus:outline-none focus:border-bronze";
const labelCls = "label-eyebrow block mb-1.5";

/* ----------------------------- NEWS MANAGER ----------------------------- */
const EMPTY_NEWS = { title: "", publication: "", date: "", excerpt: "", body: "", image_url: "", type: "internal", section: "newsroom", external_url: "", embed_html: "", featured: false, published: true };

function NewsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // article object or null
  const [form, setForm] = useState(EMPTY_NEWS);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/news?published_only=false").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY_NEWS); setShowForm(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY_NEWS, ...a }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/news/${editing.id}`, form);
      else await api.post("/admin/news", form);
      toast.success("Story saved");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this story?")) return;
    await api.delete(`/admin/news/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-news-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl text-ink">News & Blog Stories</h2>
        <button onClick={openNew} data-testid="admin-news-new" className="inline-flex items-center gap-2 rounded-full bg-bronze px-5 py-2.5 font-body text-sm text-ivory hover:bg-ink transition-colors">
          <Plus size={16} /> New Story
        </button>
      </div>

      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} data-testid={`admin-news-row-${a.id}`} className="rounded-2xl bg-white border border-champagne/30 p-5 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`rounded-full px-2.5 py-0.5 text-[0.65rem] uppercase tracking-wide ${a.section === "blog" ? "bg-bronze/20 text-bronze" : "bg-ink/10 text-ink/60"}`}>{a.section === "blog" ? "Blog" : "Newsroom"}</span>
                <span className={`rounded-full px-2.5 py-0.5 text-[0.65rem] uppercase tracking-wide ${a.type === "external" ? "bg-champagne/30 text-bronze" : "bg-blush/30 text-bronze"}`}>{a.type}</span>
                {a.featured && <span className="rounded-full bg-bronze/15 text-bronze px-2.5 py-0.5 text-[0.65rem] uppercase tracking-wide">Featured</span>}
                {!a.published && <span className="rounded-full bg-ink/10 text-ink/60 px-2.5 py-0.5 text-[0.65rem] uppercase tracking-wide">Draft</span>}
              </div>
              <p className="mt-1.5 font-heading text-lg text-ink truncate">{a.title}</p>
              <p className="font-body text-xs text-ink/50">{a.publication} {a.date && `· ${a.date}`}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openEdit(a)} data-testid={`admin-news-edit-${a.id}`} className="p-2.5 rounded-full hover:bg-blush/15 text-ink/70"><Pencil size={16} /></button>
              <button onClick={() => remove(a.id)} data-testid={`admin-news-delete-${a.id}`} className="p-2.5 rounded-full hover:bg-destructive/10 text-destructive"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="font-body text-ink/50">No stories yet.</p>}
      </div>

      {showForm && (
        <Modal title={editing ? "Edit Story" : "New Story"} onClose={() => setShowForm(false)}>
          <form onSubmit={save} data-testid="admin-news-form" className="space-y-4">
            <div>
              <label className={labelCls}>Title</label>
              <input className={inputCls} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="news-form-title" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Publication</label><input className={inputCls} value={form.publication} onChange={(e) => setForm({ ...form, publication: e.target.value })} data-testid="news-form-publication" /></div>
              <div><label className={labelCls}>Date</label><input type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="news-form-date" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Section</label>
                <select className={inputCls} value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} data-testid="news-form-section">
                  <option value="newsroom">Newsroom (press coverage)</option>
                  <option value="blog">Blog (Leah's posts)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Type</label>
                <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="news-form-type">
                  <option value="internal">Internal (write the story here)</option>
                  <option value="external">External (link / embed published article)</option>
                </select>
              </div>
            </div>
            <div><label className={labelCls}>Featured Image</label><ImageUploader value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} testid="news-form-image" /></div>
            <div><label className={labelCls}>Excerpt</label><textarea rows={2} className={inputCls} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="news-form-excerpt" /></div>
            {form.type === "internal" ? (
              <div><label className={labelCls}>Body</label><RichTextEditor value={form.body} onChange={(html) => setForm({ ...form, body: html })} /></div>
            ) : (
              <>
                <div><label className={labelCls}>External Article URL</label><input className={inputCls} value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} data-testid="news-form-external" placeholder="https://publication.com/article" /></div>
                <div><label className={labelCls}>Embed HTML (optional)</label><textarea rows={3} className={inputCls} value={form.embed_html} onChange={(e) => setForm({ ...form, embed_html: e.target.value })} data-testid="news-form-embed" placeholder="<iframe …></iframe>" /></div>
              </>
            )}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 font-body text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="news-form-featured" /> Featured</label>
              <label className="flex items-center gap-2 font-body text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="news-form-published" /> Published</label>
            </div>
            <button type="submit" data-testid="news-form-save" className="w-full rounded-full bg-bronze px-6 py-3.5 font-body text-sm font-semibold text-ivory hover:bg-ink transition-colors">Save Story</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ----------------------------- RESULTS MANAGER ----------------------------- */
const EMPTY_RESULT = { event: "", meet: "", result: "", date: "", kind: "result", link: "", order: 0 };

function ResultsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_RESULT);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/results").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY_RESULT); setShowForm(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY_RESULT, ...a }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing) await api.put(`/admin/results/${editing.id}`, payload);
      else await api.post("/admin/results", payload);
      toast.success("Result saved");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/admin/results/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-results-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl text-ink">Latest Results & Upcoming</h2>
        <button onClick={openNew} data-testid="admin-result-new" className="inline-flex items-center gap-2 rounded-full bg-bronze px-5 py-2.5 font-body text-sm text-ivory hover:bg-ink transition-colors">
          <Plus size={16} /> New Entry
        </button>
      </div>

      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} data-testid={`admin-result-row-${a.id}`} className="rounded-2xl bg-white border border-champagne/30 p-5 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <span className={`rounded-full px-2.5 py-0.5 text-[0.65rem] uppercase tracking-wide ${a.kind === "upcoming" ? "bg-champagne/30 text-bronze" : "bg-blush/30 text-bronze"}`}>{a.kind === "upcoming" ? "Upcoming" : "Result"}</span>
              <p className="mt-1.5 font-heading text-lg text-ink truncate">{a.event}</p>
              <p className="font-body text-xs text-ink/50 truncate">{a.meet} {a.result && `· ${a.result}`} {a.date && `· ${a.date}`}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => openEdit(a)} data-testid={`admin-result-edit-${a.id}`} className="p-2.5 rounded-full hover:bg-blush/15 text-ink/70"><Pencil size={16} /></button>
              <button onClick={() => remove(a.id)} data-testid={`admin-result-delete-${a.id}`} className="p-2.5 rounded-full hover:bg-destructive/10 text-destructive"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="font-body text-ink/50">No entries yet. Add race results and upcoming meets shown in The Journey.</p>}
      </div>

      {showForm && (
        <Modal title={editing ? "Edit Entry" : "New Entry"} onClose={() => setShowForm(false)}>
          <form onSubmit={save} data-testid="admin-result-form" className="space-y-4">
            <div>
              <label className={labelCls}>Type</label>
              <select className={inputCls} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="result-form-kind">
                <option value="result">Latest Result</option>
                <option value="upcoming">Upcoming Meet</option>
              </select>
            </div>
            <div><label className={labelCls}>Event</label><input className={inputCls} required value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })} data-testid="result-form-event" placeholder="400m Final" /></div>
            <div><label className={labelCls}>Meet</label><input className={inputCls} value={form.meet} onChange={(e) => setForm({ ...form, meet: e.target.value })} data-testid="result-form-meet" placeholder="2026 MVC Outdoor Championships" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Result {form.kind === "upcoming" && "(optional)"}</label><input className={inputCls} value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })} data-testid="result-form-result" placeholder="1st · 22.69" /></div>
              <div><label className={labelCls}>Date</label><input className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="result-form-date" placeholder="May 2026" /></div>
            </div>
            <div><label className={labelCls}>Order</label><input type="number" className={inputCls} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="result-form-order" /></div>
            <div><label className={labelCls}>Results Link (optional)</label><input className={inputCls} value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="result-form-link" placeholder="https://results.example.com/..." /></div>
            <button type="submit" data-testid="result-form-save" className="w-full rounded-full bg-bronze px-6 py-3.5 font-body text-sm font-semibold text-ivory hover:bg-ink transition-colors">Save Entry</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ----------------------------- SPONSOR MANAGER ----------------------------- */
const EMPTY_SPONSOR = { name: "", logo_url: "", website: "", tier: "supporting", blurb: "", order: 0 };

function SponsorManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SPONSOR);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/sponsors").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY_SPONSOR); setShowForm(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY_SPONSOR, ...a }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing) await api.put(`/admin/sponsors/${editing.id}`, payload);
      else await api.post("/admin/sponsors", payload);
      toast.success("Sponsor saved");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this sponsor?")) return;
    await api.delete(`/admin/sponsors/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div data-testid="admin-sponsor-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl text-ink">Sponsors</h2>
        <button onClick={openNew} data-testid="admin-sponsor-new" className="inline-flex items-center gap-2 rounded-full bg-bronze px-5 py-2.5 font-body text-sm text-ivory hover:bg-ink transition-colors">
          <Plus size={16} /> Add Sponsor
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((a) => (
          <div key={a.id} data-testid={`admin-sponsor-row-${a.id}`} className="rounded-2xl bg-white border border-champagne/30 p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="h-12 w-12 shrink-0 rounded-lg bg-ivory border border-champagne/30 flex items-center justify-center overflow-hidden">
                {a.logo_url ? <img src={a.logo_url} alt={a.name} className="max-h-10 max-w-10 object-contain" /> : <Award size={18} className="text-bronze" />}
              </div>
              <div className="min-w-0"><p className="font-heading text-base text-ink truncate">{a.name} <span className="ml-1 rounded-full bg-blush/25 text-bronze px-2 py-0.5 text-[0.6rem] uppercase tracking-wide align-middle">{a.tier}</span></p><p className="font-body text-xs text-ink/50 truncate">{a.website || "No link"}</p></div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => openEdit(a)} data-testid={`admin-sponsor-edit-${a.id}`} className="p-2.5 rounded-full hover:bg-blush/15 text-ink/70"><Pencil size={16} /></button>
              <button onClick={() => remove(a.id)} data-testid={`admin-sponsor-delete-${a.id}`} className="p-2.5 rounded-full hover:bg-destructive/10 text-destructive"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="font-body text-ink/50">No sponsors yet. Add sponsor logos to feature them in the Sponsorship section.</p>}
      </div>

      {showForm && (
        <Modal title={editing ? "Edit Sponsor" : "Add Sponsor"} onClose={() => setShowForm(false)}>
          <form onSubmit={save} data-testid="admin-sponsor-form" className="space-y-4">
            <div><label className={labelCls}>Sponsor Name</label><input className={inputCls} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sponsor-form-name" /></div>
            <div>
              <label className={labelCls}>Tier</label>
              <select className={inputCls} value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="sponsor-form-tier">
                <option value="featured">Featured (homepage + big card + link)</option>
                <option value="supporting">Supporting (sponsors page + link)</option>
                <option value="community">Community (sponsors page, no link)</option>
              </select>
            </div>
            <div><label className={labelCls}>Blurb (featured only, optional)</label><textarea rows={3} className={inputCls} value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} data-testid="sponsor-form-blurb" placeholder="Short description shown on featured sponsor cards." /></div>
            <div><label className={labelCls}>Logo URL</label><input className={inputCls} value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} data-testid="sponsor-form-logo" placeholder="https://…/logo.png" /><p className="mt-1.5 font-body text-xs text-ink/50">Paste a direct link to the logo image (PNG with transparent background works best).</p></div>
            <div><label className={labelCls}>Website (optional)</label><input className={inputCls} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="sponsor-form-website" placeholder="https://sponsor.com" /></div>
            <div><label className={labelCls}>Order</label><input type="number" className={inputCls} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="sponsor-form-order" /></div>
            <button type="submit" data-testid="sponsor-form-save" className="w-full rounded-full bg-bronze px-6 py-3.5 font-body text-sm font-semibold text-ivory hover:bg-ink transition-colors">Save Sponsor</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ----------------------------- NEWSLETTER ----------------------------- */
function NewsletterList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/newsletter").then(({ data }) => setItems(data)).catch(() => {}); }, []);

  const exportCsv = () => {
    const rows = ["email,subscribed_at", ...items.map((i) => `${i.email},${i.created_at}`)];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "pink-skye-journal-subscribers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-newsletter-list">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl text-ink">Subscribers <span className="text-bronze">({items.length})</span></h2>
        <button onClick={exportCsv} data-testid="admin-newsletter-export" className="rounded-full border border-champagne/40 px-5 py-2.5 font-body text-sm hover:bg-blush/15 transition-colors">Export CSV</button>
      </div>
      <div className="rounded-2xl bg-white border border-champagne/30 divide-y divide-champagne/20">
        {items.map((i) => (
          <div key={i.id} className="px-5 py-3.5 flex items-center justify-between">
            <span className="font-body text-sm text-ink">{i.email}</span>
            <span className="font-body text-xs text-ink/40">{new Date(i.created_at).toLocaleDateString()}</span>
          </div>
        ))}
        {items.length === 0 && <p className="px-5 py-6 font-body text-ink/50">No subscribers yet.</p>}
      </div>
    </div>
  );
}

/* ----------------------------- INQUIRIES ----------------------------- */
function InquiriesList() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/inquiries").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const unread = items.filter((i) => !i.read).length;

  const markRead = async (id) => {
    await api.put(`/admin/inquiries/${id}/read`);
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, read: true } : i)));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/inquiries/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-inquiries-list">
      <h2 className="font-heading text-2xl text-ink mb-6">
        Inquiries <span className="text-bronze">({items.length})</span>
        {unread > 0 && <span className="ml-2 align-middle rounded-full bg-bronze text-ivory px-2.5 py-0.5 text-xs" data-testid="inquiries-unread-badge">{unread} new</span>}
      </h2>
      <div className="space-y-3">
        {items.map((i) => (
          <div key={i.id} data-testid={`inquiry-row-${i.id}`} className={`rounded-2xl border p-5 ${i.read ? "bg-white border-champagne/30" : "bg-blush/10 border-bronze/40"}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                {!i.read && <span className="h-2 w-2 rounded-full bg-bronze" title="Unread" />}
                <span className="rounded-full bg-blush/30 text-bronze px-2.5 py-0.5 text-[0.65rem] uppercase tracking-wide">{i.inquiry_type}</span>
                <p className="font-heading text-lg text-ink">{i.name}</p>
              </div>
              <span className="font-body text-xs text-ink/40">{new Date(i.created_at).toLocaleString()}</span>
            </div>
            <p className="mt-1 font-body text-sm text-bronze">
              <a href={`mailto:${i.email}`} className="hover:underline">{i.email}</a>{i.organization && ` · ${i.organization}`}
            </p>
            <p className="mt-3 font-body text-sm text-ink/75 whitespace-pre-line">{i.message}</p>
            <div className="mt-4 flex items-center gap-2">
              <a href={`mailto:${i.email}?subject=Re: Your ${i.inquiry_type} inquiry`} data-testid={`inquiry-reply-${i.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-bronze px-4 py-2 font-body text-xs text-ivory hover:bg-ink transition-colors"><Mail size={13} /> Reply</a>
              {!i.read && <button onClick={() => markRead(i.id)} data-testid={`inquiry-read-${i.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-champagne/50 px-4 py-2 font-body text-xs text-ink/70 hover:bg-blush/15 transition-colors"><Check size={13} /> Mark read</button>}
              <button onClick={() => remove(i.id)} data-testid={`inquiry-delete-${i.id}`} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-body text-xs text-destructive hover:bg-destructive/10 transition-colors ml-auto"><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="font-body text-ink/50">No inquiries yet.</p>}
      </div>
    </div>
  );
}

/* ----------------------------- SETTINGS ----------------------------- */
function SettingsPanel() {
  const [form, setForm] = useState({ ga4_id: "", instagram_url: "", tiktok_url: "", instagram_handle: "", tiktok_handle: "", instagram_embed_code: "" });

  useEffect(() => { api.get("/settings").then(({ data }) => setForm((f) => ({ ...f, ...data }))).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  return (
    <div data-testid="admin-settings-panel" className="max-w-xl">
      <h2 className="font-heading text-2xl text-ink mb-6">Site Settings</h2>
      <form onSubmit={save} className="space-y-5 rounded-2xl bg-white border border-champagne/30 p-7">
        <div>
          <label className={labelCls}>Google Analytics 4 Measurement ID</label>
          <input className={inputCls} value={form.ga4_id || ""} onChange={(e) => setForm({ ...form, ga4_id: e.target.value })} data-testid="settings-ga4" placeholder="G-XXXXXXXXXX" />
          <p className="mt-1.5 font-body text-xs text-ink/50">Paste your GA4 ID here. Analytics will load automatically on the public site.</p>
        </div>
        <div>
          <label className={labelCls}>Instagram Feed Embed Code</label>
          <textarea rows={5} className={inputCls} value={form.instagram_embed_code || ""} onChange={(e) => setForm({ ...form, instagram_embed_code: e.target.value })} data-testid="settings-ig-embed" placeholder="Paste embed code from SnapWidget, Behold, etc." />
          <p className="mt-1.5 font-body text-xs text-ink/50">Paste the embed code from your Instagram widget service (e.g. Behold.so or SnapWidget). It renders a live, auto-updating feed in the "Follow The Journey" section.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Instagram URL</label><input className={inputCls} value={form.instagram_url || ""} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} data-testid="settings-ig-url" /></div>
          <div><label className={labelCls}>Instagram Handle</label><input className={inputCls} value={form.instagram_handle || ""} onChange={(e) => setForm({ ...form, instagram_handle: e.target.value })} data-testid="settings-ig-handle" /></div>
          <div><label className={labelCls}>TikTok URL</label><input className={inputCls} value={form.tiktok_url || ""} onChange={(e) => setForm({ ...form, tiktok_url: e.target.value })} data-testid="settings-tt-url" /></div>
          <div><label className={labelCls}>TikTok Handle</label><input className={inputCls} value={form.tiktok_handle || ""} onChange={(e) => setForm({ ...form, tiktok_handle: e.target.value })} data-testid="settings-tt-handle" /></div>
        </div>
        <button type="submit" data-testid="settings-save" className="w-full rounded-full bg-bronze px-6 py-3.5 font-body text-sm font-semibold text-ivory hover:bg-ink transition-colors">Save Settings</button>
      </form>
    </div>
  );
}

/* ----------------------------- MODAL ----------------------------- */
function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/40 backdrop-blur-sm p-4 py-10" onClick={onClose}>
      <div className="w-full max-w-lg rounded-[2rem] bg-ivory border border-champagne/30 p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-2xl text-ink">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="p-2 rounded-full hover:bg-blush/15 text-ink/60"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
